import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Typography } from 'antd';
import { FC } from 'react';
import {
  Control,
  FieldArrayWithId,
  get,
  useFormContext,
} from 'react-hook-form';

import { EmptyState } from './BenefitsList';
import { FormItem } from './FormItem';
import { StarRating } from './StarRating';

const { TextArea } = Input;
const { Text } = Typography;

const MAX_REVIEWS = 3;

interface ReviewsListProps {
  control: Control<any>;
  fields: FieldArrayWithId[];
  append: (value: { rating: number; comment: string; author: string }) => void;
  remove: (index: number) => void;
}

export const ReviewsList: FC<ReviewsListProps> = ({
  control,
  fields,
  append,
  remove,
}) => {
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <>
      <Flex justify='flex-end' style={{ marginBottom: 8 }}>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => append({ rating: 0, comment: '', author: '' })}
          disabled={fields.length >= MAX_REVIEWS}
        >
          Adicionar Depoimento
        </Button>
      </Flex>

      {fields.length === 0 ? (
        <EmptyState
          title='Nenhuma avaliação adicionada'
          description='Clique em "Adicionar Avaliação" para começar'
        />
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 16,
            overflowX: 'auto',
            paddingBottom: 4,
          }}
        >
          {fields.map((field, index) => (
            <div key={field.id} style={{ flex: '1' }}>
              <ReviewCard
                index={index}
                control={control}
                errors={errors}
                onRemove={() => remove(index)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

interface ReviewCardProps {
  index: number;
  control: Control<any>;
  errors: any;
  onRemove: () => void;
}

const ReviewCard: FC<ReviewCardProps> = ({
  index,
  control,
  errors,
  onRemove,
}) => (
  <Flex
    vertical
    gap={16}
    style={{
      padding: 16,
      background: '#FFFFFF14',
      borderRadius: 8,
      border: '1px solid #424242',
    }}
  >
    <Flex justify='space-between' align='center'>
      <Text strong>Classificação (1-5 estrelas)</Text>
      <Button type='link' danger icon={<DeleteOutlined />} onClick={onRemove}>
        Remover
      </Button>
    </Flex>

    <FormItem
      control={control}
      name={`content.reviews.${index}.rating`}
      renderItem={({ field }: any) => (
        <StarRating value={field.value || 0} onChange={field.onChange} />
      )}
    />

    <FormItem
      label='Texto da Avaliação'
      control={control}
      name={`content.reviews.${index}.comment`}
      renderItem={({ field }: any) => (
        <TextArea
          {...field}
          placeholder='Escreva o depoimento do cliente...'
          rows={4}
          maxLength={600}
          showCount
          status={
            get(errors, `content.reviews.${index}.comment`)
              ? 'error'
              : undefined
          }
        />
      )}
    />

    <FormItem
      label='Nome do Cliente'
      control={control}
      name={`content.reviews.${index}.author`}
      renderItem={({ field }: any) => (
        <Input
          {...field}
          placeholder='Nome do cliente'
          maxLength={40}
          showCount
          status={
            get(errors, `content.reviews.${index}.author`) ? 'error' : undefined
          }
        />
      )}
    />
  </Flex>
);
